import { useMediaQuery, TableContainer, Paper, TableHead, TableRow, TableBody, Theme } from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import ConfirmationDialog from "../../../components/Misc/Dialog/ConfirmationDialog";
import { IICAOPositionalWarnings } from "../../../types/FaceVerification";
import { ISelectValue } from "../components/SelectCell";
import SelectCheckmarksCell from "../components/SelectCheckmarksCell";
import { ISliderInterface } from "../components/SliderCell";
import SliderRow from "../components/SliderRow";
import TextCell from "../components/TextCell";
import { settingUseStyles, SettingTable, TableCellButton, DefaultButton } from "../Settings";

interface IFaceQualityProps {
    findDefaultFVSliderInterface: (name: string) => ISliderInterface | undefined,
    findFVSliderInterface: (name: string) => (ISliderInterface | undefined),
    setFVSliderInterfaceOption: (op: ISliderInterface, basic?: boolean | undefined) => void
    resetValueToDefault: (name: string, basic?: boolean) => void,
    setOption: (key: string, value: string | number | boolean, basic?: boolean) => void,
    icaoWarningFilter: string,
}

const FaceQuality: React.FC<IFaceQualityProps> = (props) => {

    const classes = settingUseStyles()
    const { enqueueSnackbar } = useSnackbar();

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [openDialog, setOpenDialog] = useState<boolean>(false)

    const getEnumValuesAndKey = (): ISelectValue[] => {
        return Object.keys(IICAOPositionalWarnings).map((e) => {
            return {key: e, value: IICAOPositionalWarnings[e as keyof typeof IICAOPositionalWarnings]}
        }).filter((el) => el !== undefined) as unknown as ISelectValue[]
    }

    const setFilter = (str: string) => {
        props.setOption("IcaoWarningsFilter", str)
    }

    const handleResetClick = () => {
        enqueueSnackbar("Face quality settings reset", {variant: "info"})
        props.resetValueToDefault("IcaoWarningsFilter")
        props.resetValueToDefault("IcaoHeadMovementThreshold")
        props.resetValueToDefault("IcaoImageResolutionThreshold")
        props.resetValueToDefault("IcaoResolutionThreshold")
        props.resetValueToDefault("IcaoOcclusionThreshold")
        props.resetValueToDefault("IcaoMotionBlurThreshold")
        props.resetValueToDefault("IcaoCompressionArtifactsThreshold")
        props.resetValueToDefault("IcaoNoiseThreshold")
        props.resetValueToDefault("IcaoOverexposureThreshold")
        props.resetValueToDefault("IcaoUnderexposureThreshold")
        props.resetValueToDefault("IcaoSharpnessThreshold")
        props.resetValueToDefault("IcaoSaturationThreshold")
        props.resetValueToDefault("IcaoGrayscaleDensityThreshold")
        props.resetValueToDefault("IcaoContrastThreshold")
    }

    const getResolutionFromConfidence = (resolutionConfidenceNormed: number): number => {
        if (resolutionConfidenceNormed <= 0) {
            return 0;
        }

        const denominator = (-173.18319 / (resolutionConfidenceNormed - 146.4896)) - 1;
        if (denominator <= 0) {
            return 0;
        }
    
        return 737168.9 * Math.pow(denominator, 1 / 0.4030241);
    }
    
    const valueLabelResolution = (value: number): string => {
        let pixels = getResolutionFromConfidence(value)
        let label = ""
        if (pixels < 36864)         label = "<144p"  // 256 * 144
        else if (pixels < 102240)   label = ">144p"  // 426 * 240
        else if (pixels < 172800)   label = ">240p"  // 480 * 360
        else if (pixels < 345600)   label = ">360p"  // 720 * 480
        else if (pixels < 414720)   label = ">480p"  // 720 * 576
        else if (pixels < 921600)   label = ">576p"  // 1280 * 720
        else if (pixels < 2073600)  label = ">720p"  // 1920 * 1080
        else if (pixels < 3686400)  label = ">1080p" // 2560 * 1440
        else if (pixels < 8294400)  label = ">1440p" // 3840 * 2160
        else if (pixels < 33177600) label = ">2160p" // 7680 * 4320
        else label = ">4320p"
        return label
    }

    return (
        <>
        <TableContainer component={Paper} className={classes.container}>
            <SettingTable>
                <TableHead>
                    <TableRow>
                        <TextCell width="50%" head title="Face quality" subtitle=""/>
                        <TableCellButton align="right" colSpan={isMobile? 2 : 3}>
                            <DefaultButton variant="text" color="primary" onClick={() => setOpenDialog(true)}>
                                Reset to default
                            </DefaultButton>
                        </TableCellButton>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TextCell title="Warnings filter" subtitle="A list of positional warnings to ignore."/>
                            <SelectCheckmarksCell 
                                title="Filter"
                                active
                                colSpan={isMobile? 2 : 3}
                                menuItems={getEnumValuesAndKey()}
                                value={props.icaoWarningFilter}
                                setFilter={setFilter}
                            />
                    </TableRow>
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Head movement"
                        subtitle="Determines whether subject's head is currently moving."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoHeadMovementThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoHeadMovementThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active
                        lockMaxValue
                        info="Produces a value on [0, 100] with a score of 0 suggesting no head movements, ideal for face capture, whereas a score of 100 represents very intensive head movement often leading to a blurry out of focus image."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Image resolution"
                        subtitle="Determines whether the resolution is above the required threshold and prioritizes frames that meet or exceed this threshold when stream contains multiple resolutions."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoImageResolutionThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoImageResolutionThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        valueLabel={valueLabelResolution}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active
                        lockMaxValue
                        info="Produces a value on [0, 100] with a score of 0 indicating the image fails to meet the minimum resolution for stream capture, while a score of 100 represents an ideal 4K resolution scenario."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Resolution"
                        subtitle="Determines how accurately an image captures the fine details of the human face, indicating any deviation from perfection."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoResolutionThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoResolutionThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active
                        lockMaxValue
                        info="Highest values are set to an uncompressed image with IED of 256 pixels or higher that is perfectly focused and in all respects pristine. Produces a value on [0, 100] with higher values indicating a face is more detailed and sharp."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Occlusion"
                        subtitle="Determines the area of the face that is occluded. The face region extends from top of forehead to chin, and from ear to ear. Ignores transparent eye-glasses and frames."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoOcclusionThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoOcclusionThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active
                        lockMaxValue
                        info="Produces a value on [0, 100] with higher values indicating a percentage of face that is occluded by some object"
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Motion blur"
                        subtitle="Determines the extent to which motion blur affects the face in an image. Output should not report motion blur for an image affected by solely de-focus, or high compression."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoMotionBlurThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoMotionBlurThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active
                        lockMaxValue
                        info="Produces a value on [0, 100] with higher values indicating more motion blur is present in the face area."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Compression artifacts"
                        subtitle="Determines the presence of lossy compression artifacts (for JPEG these exist on an 8x8 grid)."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoCompressionArtifactsThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoCompressionArtifactsThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active
                        lockMaxValue
                        info="Produces a value on [0, 100] with higher values indicating that more compression artifacts are present in the face area."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Noise"
                        subtitle="Determines random variation of brightness or color information in image."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoNoiseThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoNoiseThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active
                        lockMaxValue
                        info="Produces a value on [0, 100] with higher values indicating more noise is present."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Overexposure"
                        subtitle="Determines the score representing proportion of face that is overexposed."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoOverexposureThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoOverexposureThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active
                        lockMaxValue
                        info="Higher values indicating that bigger proportion of face is overexposed"
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Underexposure"
                        subtitle="Determines the score representing proportion of face that is underexposed."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoUnderexposureThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoUnderexposureThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active
                        lockMaxValue
                        info="Produces a value on [0, 100] with higher values indicating that bigger proportion of face is underexposed."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Sharpness"
                        subtitle="Determines the amount of detail an imaging system can reproduce."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoSharpnessThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoSharpnessThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active
                        lockMaxValue
                        info="Produces a value on [0, 100] with higher values indicating better sharpness."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Saturation"
                        subtitle="Determines the intensity or purity of a color."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoSaturationThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoSaturationThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active
                        lockMaxValue
                        info="Produces value on [0, 100], with 0 representing a completely desaturated or grayscale image, and 100 indicating full, vibrant color saturation. A higher score denotes better or more vivid saturation, making the colors appear more vibrant and pure."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Grayscale density"
                        subtitle="Determines the density of black and white pixels."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoGrayscaleDensityThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoGrayscaleDensityThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active
                        lockMaxValue
                        info="Produces a value on [0, 100] with higher grayscale density."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Contrast"
                        subtitle="Determines the contradiction in luminance or colour that makes an object distinguishable."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoContrastThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoContrastThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active
                        lockMaxValue
                        info="Produces a value on [0, 100] with higher values indicating better image color contrast."
                    />
                </TableBody>
            </SettingTable>
        </TableContainer>
        <ConfirmationDialog
            open={openDialog}
            setOpen={setOpenDialog}
            title="Reset face quality settings"
            text="Reset face quality settings to default values?"
            cancelText="Cancel"
            confirmText="Yes"
            confirm={handleResetClick}
            confirmColor="error"
        />
        </>
    )
}

export default FaceQuality